/* Material */
import useStyles from './styles'

/* Types */
import { imageSrc } from 'src/shared/frontendTypes/images'
import { CSSProperties, useEffect, useRef } from 'react'
import { observePreviewImage, PreviewImageSources } from './adaptivePreviewImage'

/* Functions */
import getImageUrl from 'src/pages/Product/functions/imageUrl'
import getImageAlt from 'src/pages/Product/functions/imageAlt'

/* Components */
import AiGeneratedBadge from 'src/components/AiGeneratedBadge'
import noImage from 'src/images/camera.svg'

const ImagesCarousel: React.FC<{
  images:imageSrc[], 
  productID:number, 
  metaTitle:string,
  heightPx?: number,
  size?: 'xs' | 'sm' | 'md',
  aspectRatio?: string,
  imageMaxSizePercent?: number,
}> = ({ 
  images, 
  productID, 
  metaTitle, 
  heightPx,
  size = 'sm',
  aspectRatio,
  imageMaxSizePercent = 100,
}) => {
  const classes = useStyles()
  const imageRef = useRef<HTMLImageElement>(null)

  const image = images?.[0]
  const imagePath = getImageUrl(image, productID, size)
  const xs = getImageUrl(image, productID, 'xs') || undefined
  const sm = getImageUrl(image, productID, 'sm') || undefined
  const md = getImageUrl(image, productID, 'md') || undefined
  const fallback = imagePath || noImage.src
  const initialSource = xs || fallback
  const alt = getImageAlt(image, metaTitle)

  useEffect(() => {
    if (!imageRef.current || !imagePath) return
    const sources: PreviewImageSources = { xs, sm, md }
    return observePreviewImage(imageRef.current, sources, fallback)
  }, [xs, sm, md, fallback, imagePath])

  const style: CSSProperties = {
    maxWidth: `${imageMaxSizePercent}%`,
    maxHeight: `${imageMaxSizePercent}%`,
    width: `${imageMaxSizePercent}%`,
    height: `${imageMaxSizePercent}%`,
    objectFit: 'contain',
    objectPosition: 'center',
    padding: '0.55rem',
    backgroundColor: '#fff',
    display: 'block',
    boxSizing: 'border-box',
  }

  return <div
    className={classes.cardMedia}
    title={metaTitle}
    style={{
      ...(heightPx ? { height: heightPx, aspectRatio: 'auto' } : {}),
      ...(aspectRatio ? { aspectRatio } : {}),
    }}
  >
    <img
      key={initialSource}
      ref={imageRef}
      data-preview-image
      alt={alt}
      src={initialSource}
      style={style}
      loading='lazy'
      decoding='async'
    />
    <noscript>
      <style>{'[data-preview-image]{display:none!important}'}</style>
      <img alt={alt} src={fallback} style={style} loading='lazy' decoding='async' />
    </noscript>
    <AiGeneratedBadge aiGenerated={image?.aiGenerated} />
  </div>

  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

}

export default ImagesCarousel
